// src/components/AttivitaScadenzeWidgetContainer.jsx
import React, { useEffect, useMemo, useState } from "react";
import AttivitaScadenzeWidget from "./AttivitaScadenzeWidget";

function apiMonthKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}
function firstDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    return d.toISOString().slice(0, 10);
}
function lastDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return d.toISOString().slice(0, 10);
}

async function apiGet(url) {
    const r = await fetch(url, { credentials: "include" });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}
async function apiPost(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}

// mapping: widget priority -> DB priority 1..4
function mapPriorityToDb(p) {
    if (p === "high") return 4;     // urgente
    if (p === "medium") return 3;   // alta / in scadenza
    return 2;                       // normale
}
function mapDbPriorityToWidget(n) {
    const p = Number(n || 2);
    if (p >= 4) return "high";
    if (p === 3) return "medium";
    return "normal";
}

// mapping DB -> widget item
function mapTaskRowToWidgetItem(t) {
    const dateStr = t.target_date || t.due_date || new Date().toISOString().slice(0, 10);
    return {
        id: t.id,
        label: t.title,
        date: dateStr,
        priority: mapDbPriorityToWidget(t.priority),
        urgent: Number(t.priority) >= 4,
        warning: Number(t.priority) === 3,
        metaDate: t.due_date ? `Scade: ${t.due_date}` : null,
        assignee: t.assigned_to ? `Utente #${t.assigned_to}` : null,
        raw: t,
    };
}

export default function AttivitaScadenzeWidgetContainer() {
    const [month, setMonth] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [tasksOpen, setTasksOpen] = useState([]);
    const [tasksDone, setTasksDone] = useState([]);
    const [dotsMap, setDotsMap] = useState(new Map());

    const monthKey = useMemo(() => apiMonthKey(month), [month]);
    const from = useMemo(() => firstDay(month), [month]);
    const to = useMemo(() => lastDay(month), [month]);

    async function reloadAll() {
        setLoading(true);
        setErr("");
        try {
            // 1) open tasks
            const open = await apiGet(
                `/api/tasks/list.php?from=${from}&to=${to}&status=open&date_field=target`
            );
            // 2) done tasks
            const done = await apiGet(
                `/api/tasks/list.php?from=${from}&to=${to}&status=done&date_field=target`
            );
            // 3) dots
            const dots = await apiGet(`/api/tasks/dots.php?month=${monthKey}`);

            setTasksOpen((open.tasks || []).map(mapTaskRowToWidgetItem));
            setTasksDone((done.tasks || []).map((t) => ({
                ...mapTaskRowToWidgetItem(t),
                completedAt: t.completed_at ? new Date(t.completed_at).getTime() : Date.now(),
            })));

            // dots: { "YYYY-MM-DD": {count, tone(1..4)} }
            const m = new Map();
            const obj = dots.dots || {};
            Object.keys(obj).forEach((day) => {
                const toneNum = Number(obj[day]?.tone || 2);
                const tone =
                    toneNum >= 4 ? "red" : toneNum === 3 ? "orange" : "blue";
                m.set(day, tone);
            });
            setDotsMap(m);
        } catch (e) {
            setErr(e.message || "Errore caricamento");
            setTasksOpen([]);
            setTasksDone([]);
            setDotsMap(new Map());
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reloadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthKey]);

    async function handleAddTask(dateStr, payload) {
        // payload: { label, priority: normal|medium|high }
        const title = (payload?.label || "").trim();
        if (!title) return;

        await apiPost("/api/tasks/create.php", {
            title,
            target_date: dateStr,
            due_date: null,
            priority: mapPriorityToDb(payload?.priority),
            source_module: "dashboard",
            source_ref: null,
            visibility: "company",
        });

        await reloadAll();
    }

    async function handleCompleteTask(item) {
        await apiPost("/api/tasks/complete.php", { id: item.id });
        await reloadAll();
    }

    // opzionale: apri dettaglio
    function handleOpenTask(item) {
        // per ora niente modal: puoi aprire una sidebar o navigare a /admin?tab=scadenze&id=...
        console.log("OPEN TASK", item);
    }

    return (
        <div>
            {err ? (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {err}
                </div>
            ) : null}

            {loading ? (
                <div className="mb-3 text-sm text-slate-500">Caricamento attività…</div>
            ) : null}

            <AttivitaScadenzeWidget
                tasks={tasksOpen}
                done={tasksDone}
                scadenzeByDate={dotsMap}
                onOpenTask={handleOpenTask}
                onCompleteTask={handleCompleteTask}
                onAddTask={handleAddTask}
            // onRestoreTask: lo facciamo dopo se vuoi (serve endpoint restore)
            />

            {/* piccolo hook: quando cambi mese nel DayPicker, dobbiamo aggiornare anche qui.
          Quindi ti serve una micro-modifica nel widget per esporre onMonthChange */}
        </div>
    );
}